import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import MetaTags from 'react-meta-tags';
import SweetAlert from 'react-bootstrap-sweetalert';
import { Card, CardBody, Col, Container, Row, FormGroup, Label, Alert } from 'reactstrap';
import NumberFormat from 'react-number-format';
import { AvField } from 'availity-reactstrap-validation';
import InputMask from 'react-input-mask';
import { Copy } from 'lucide-react';
import ToolkitProvider from 'react-bootstrap-table2-toolkit';
import { motion, AnimatePresence } from 'framer-motion';

import { marketingApi } from '../../services/MarketingService';

import Breadcrumbs from '../../components/Breadcrumb';
import PageLoader from '../../components/Loaders/newPageLoader';
import { showToast } from '../../components/Toast';
import useLoadingProgress from '../../hooks/useLoadingProgress';
import { MainContent } from '../../components/Table/MainContent';

export default function SubsList({ profileData, loading }) {
  const {
    data = [],
    isLoading,
    isFetching,
    // error,
    refetch
  } = marketingApi.useFetchAllSubsQuery(undefined, {
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false
  });

  const [addSubscription] = marketingApi.useAddSubMutation();
  const [updateSubscription] = marketingApi.useUpdateSubMutation();
  const [deleteSubscription] = marketingApi.useDeleteSubMutation();

  const [subs, setSubsData] = useState([]);
  const [expiryDate, setExpiryDate] = useState('');
  const [correctDate, setCorrectDate] = useState(true);
  const [filteredSubs, setFilteredSubs] = useState([]);
  const [filters, setNoDbFilters ] = useState([]);
  const [error, setErrorMsg] = useState('');
  const [sub, setSelectedSub] = useState([]);
  const [isEdit, setIsEdit] = useState(false);

  const { progress, step, visible } = useLoadingProgress({ isLoading, isFetching });
  useEffect(() => {
    if (data.subs) {
      setSubsData(JSON.parse(JSON.stringify(data.subs)));
    }
  }, [data]);

  const [cardHeight, setCardHeight] = useState(0);
  const cardRef = useRef(null);
  useEffect(() => {
    if (!cardRef.current) return;
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const newHeight = entry.contentRect.height;
        if (newHeight > 0) setCardHeight(newHeight);
      }
    });
    resizeObserver.observe(cardRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    const applyFilters = () => {
      let result = [...subs];
      if (filters?.paymentOptions?.length > 0) {
        result = result.filter(sub => filters?.paymentOptions.includes(sub.paymentOption));
      }

      setFilteredSubs(result);
    };

    applyFilters();
  }, [filters, subs]);  

  // Toggle modal
  const [modalNewSub, setModalNewSub] = useState(false);
  /**
   * Toggles the visibility state of the "New Subscription" modal.
   * Uses a callback to invert the current modal state.
   *
   * @function
   * @returns {void}
   */
  const toggleNewSubModal = useCallback(() => {
    setModalNewSub((prev) => !prev);
  }, []);
  /**
   * Handles the event when the "New Subscription" button is clicked.
   * Resets the selected subscription, disables edit mode, clears any error messages,
   * and opens the modal for creating a new subscription.
   */
  const handleNewSubClick = () => {
    setSelectedSub([]);
    setIsEdit(false);
    setErrorMsg('');
    toggleNewSubModal();
  };
  /**
   * Handles the event when a subscription is selected for editing.
   * Sets the selected subscription, enables edit mode, formats and sets the expiry date,
   * and toggles the modal for editing or creating a new subscription.
   *
   * @param {Object} sub - The subscription object to be edited.
   */
  const handleSubClickEdit = (sub) => {
    setSelectedSub(sub);
    setIsEdit(true);
    setExpiryDate(new Date(sub.expiryDate).toLocaleDateString('en-GB'));
    toggleNewSubModal();
  }
  /**
   * Checks if a subscription with the same service name as the provided newSub already exists in the subs array.
   * The comparison is case-sensitive and trims whitespace from the service names before comparing.
   *
   * @param {Object} newSub - The new subscription object to check.
   * @param {string} newSub.service - The name of the service for the new subscription.
   * @returns {boolean} True if a subscription with the same service name exists, false otherwise.
   */
  const checkExistingSub = (newSub) => {
    const newService = newSub.service?.trim?.(); // Safe in case newSub.service is null/undefined
    return subs.some((el) => {
      const existingService = el.service?.trim?.();
      return existingService === newService;
    });
  };
  /**
   * Handles the submission of a subscription form for both adding and editing subscriptions.
   *
   * Depending on the `isEdit` flag, this function either updates an existing subscription
   * or adds a new one. It performs validation to check for duplicate subscriptions before adding.
   * On success, it toggles the subscription modal and displays a toast notification.
   * On failure, it logs the error and shows an error toast.
   *
   * @async
   * @param {Event} e - The form submission event.
   * @param {Object} values - The form values containing subscription details.
   * @param {string} values.service - The name of the subscription service.
   * @param {string} values.link - The link to the subscription service.
   * @param {string} values.description - A description of the subscription.
   * @param {string} values.paymentOption - The payment option selected.
   * @param {number|string} values.pricePaid - The price paid for the subscription.
   * @param {string} values.comment - Additional comments about the subscription.
   * @returns {Promise<void>}
   */
  const handleSubSubmit = async (e, values) => {
    if (isEdit) {
      const updateSub = {
        _id: sub._id,
        service: values.service,
        link: values.link,
        description: values.description,
        paymentOption: values.paymentOption,
        pricePaid: values.pricePaid,
        expiryDate: expiryDate.split('/').reverse().join('-'),
        comment: values.comment
      }
      try {
        await updateSubscription(updateSub).unwrap();
        toggleNewSubModal();
        showToast('Subscription is Edited');
      } catch (error) {
        showToast('Failed to Edit Subscription', 'error');
      }
    } else {
      const newSub = {
        service: values["service"],
        link: values["link"],
        description: values["description"],
        paymentOption: values["paymentOption"],
        pricePaid: values["pricePaid"] === '' ? 0 : values["pricePaid"],
        expiryDate: expiryDate ? expiryDate.split('/').reverse().join('-') : null,
        comment: values["comment"]
      }
      try {
        if (checkExistingSub(newSub) === true) {
          setErrorMsg("Subcription already exists.");
        }else {
          await addSubscription(newSub).unwrap();
          toggleNewSubModal();
          showToast('New Subscription is Added');
        }
      } catch (error) {
        showToast('Failed to Add New Subscription', 'error');
      }
    }
  }
  const [rowToDelete, setRowToDelete] = useState(null);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  /**
   * Handles changes to an input value, typically for a date field.
   * Checks if the month portion of the input (characters at positions 3 and 4) is valid (<= 12).
   * Updates the state to indicate whether the date is correct and sets the expiry date if valid.
   *
   * @param {Object} event - The event object from the input change.
   * @param {Object} event.target - The target element of the event.
   * @param {string} event.target.value - The current value of the input field.
   */
  const onChangeValue = (event) => {
    if (parseFloat(event.target.value.substring(3, 5)) > 12) {
      setCorrectDate(false);
    } else {
      setCorrectDate(true);
      setExpiryDate(event.target.value)
    }
  };
  /**
   * Handles the delete action for a subscription item.
   * Sets the selected subscription, marks the row for deletion,
   * and displays the delete confirmation alert.
   *
   * @param {Object} sub - The subscription object to be deleted.
   */
  const handleSubClickDelete = (sub) => {
    setSelectedSub(sub);
    setRowToDelete(sub._id);
    setShowDeleteAlert(true);
  };
  /**
   * Handles the deletion of a user subscription.
   * 
   * This asynchronous function attempts to delete the currently selected subscription.
   * On success, it resets relevant UI states and displays a success toast notification.
   * On failure, it logs the error and displays an error toast notification.
   *
   * @async
   * @function
   * @returns {Promise<void>} Resolves when the deletion process is complete.
   */
  const handleDeleteSub = async () => {
    try {
      // setIsDeleting(true);
      await deleteSubscription(rowToDelete);
      setRowToDelete(null);
      setShowDeleteAlert(false);
      setSelectedSub([]);
      showToast('Subscription Deleted Successfully');
    } catch (error) {
      showToast('Failed to delete row', 'error');
    }
  };
  /**
   * Renders the modal content for the subscription form, including fields for service name, website, description,
   * payment option, price paid, expiry date, and comment. Utilizes AvField components for validation and InputMask
   * for date input formatting. Displays validation feedback for incorrect date format.
   *
   * @constant
   * @type {JSX.Element}
   */
  const modalSubContent = (
    <>
      <Row>
        <Col md={4}>
          <FormGroup className="mb-3">
            <AvField
              name="service"
              label="Service Name"
              type="text"
              errorMessage="Invalid name"
              validate={{
                required: {
                  value: true,
                },
              }}
              value={
                sub.service || ""
              }
            />
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup className="mb-3">
            <AvField
              name="link"
              label="Website"
              type="text"
              errorMessage="Invalid name"
              validate={{
                required: {
                  value: true,
                },
              }}
              value={
                sub.link || ""
              }
            />
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup className="mb-3">
            <AvField
              name="description"
              label="Description"
              type="text"
              value={
                sub.description || ""
              }
            />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md={4}>
          <FormGroup className="mb-3">
            <AvField
              name="paymentOption"
              label="Payment Option"
              type="text"
              errorMessage="Invalid payment"
              validate={{
                required: {
                  value: true,
                },
              }}
              value={
                sub.paymentOption || ""
              }
            />
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup className='mb-3'>
            <AvField
              name='pricePaid'
              label='Price Paid'
              autoComplete='off'
              type='number'
              value={sub.pricePaid || 0}
            />
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup className='mb-3'>
            <Label>Expiry Date</Label>
            <InputMask
              mask='99/99/9999'
              value={expiryDate}
              name='expiryDate'
              onChange={onChangeValue}
              className={
                correctDate
                  ? ' form-control input-mask'
                  : 'form-control input-mask is-invalid'
              }
            />
            {!correctDate && (
              <div className='invalid-feedback'>
                Invalid Date. Please use dd/mm/yyyy format
              </div>
            )}
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <FormGroup className='mb-3'>
            <AvField
              name='comment'
              label='Comment'
              autoComplete='off'
              type='text'
              value={sub.comment || 0}
            />
          </FormGroup>
        </Col>
      </Row>
    </>
  );
  /**
   * Defines the column configuration for the subscriptions table in the Marketing page.
   *
   * Each column object specifies how the corresponding data field should be displayed,
   * formatted, and interacted with in the table. This includes custom renderers for
   * displaying service names as links, formatting currency, copying descriptions to
   * clipboard, and providing admin actions such as edit and delete. The configuration
   * also supports sorting, custom footers (such as summing amounts), and disables
   * editing where appropriate.
   *
   * @type {Array<Object>}
   * @property {string} text - The display name of the column header.
   * @property {string} dataField - The key in the data object for this column.
   * @property {boolean} [sort] - Whether the column is sortable.
   * @property {boolean} [editable] - Whether the column is editable.
   * @property {function} [formatter] - Custom function to render the cell content.
   * @property {function|string} [footer] - Custom function or string to render the column footer.
   * @property {boolean} [isDummyField] - If true, the column does not map to a data field.
   */
  const columns = [
    {
      text: 'ID',
      dataField: '_id',
      sort: true,
      editable: false,
      formatter: (cell, row, rowIndex) => rowIndex + 1,
      footer: ''
    },
    {
      text: "Service Name",
      dataField: "service",
      formatter: (cellContent, sub) => (
        <h5 className="font-size-16 mb-1">
          <a href={"https://"+sub.link} target='_blank' rel="noreferrer">
            {sub.service}
          </a>
        </h5>
      ),
      footer: ''
    },
    {
      text: "Description",
      dataField: "description",
      sort: true,
      formatter: (cellContent, sub) => (
        <div style={{whiteSpace: "initial"}}>
          <Row>
            <Col sm="1" className='ps-4 p-0 d-inline-flex align-items-center'>
              <Copy
                size={20}
                color='#497ea7'
                style={{ cursor: 'pointer' }}
                onClick={(e) => {
                  e.stopPropagation(); // Prevent unintended actions

                  const textarea = document.createElement('textarea');
                  textarea.value = sub.description;
                  textarea.style.position = 'fixed'; // Avoid scrolling
                  textarea.style.opacity = 0; // Make invisible
                  document.body.appendChild(textarea);
                  textarea.select();

                  try {
                    if (document.execCommand('copy')) {
                      showToast('success', 'Copied to clipboard'); // ✅ Show success only if copy works
                    } else {
                      showToast('error', 'Failed to copy tracking numbers.');
                    }
                  } catch (err) {
                    showToast('error', 'Failed to copy tracking numbers.');
                  }

                  document.body.removeChild(textarea); // Clean up the textarea
                }}
              />
            </Col>
            <Col sm="11">{sub.description}</Col>
          </Row>
        </div>
      ),
      footer: ''
    },
    {
      text: "Payment Option",
      dataField: "paymentOption",
      sort: true,
      formatter: (cellContent, sub) => (
        <div className='slname-width'>
          <h5 className="font-size-16 mb-1">{sub.paymentOption}</h5>
        </div>
      ),
      footer: ''
    },
    {
      text: "Amount Paid",
      dataField: "pricePaid",
      sort: true,
      formatter: (cellContent, sub) => (
        <h5 className="font-size-16 mb-1">
          <NumberFormat value={parseFloat(sub.pricePaid).toFixed(2)} displayType='text' thousandSeparator={true} prefix='$' />
        </h5>
      ),
      footer: (columnData) => (
        <div className='btn-success p-2 rounded-pill text-center'>
          <span className='font-size-16 fw-bold'>
            <NumberFormat value={columnData.reduce((acc, item) => acc + parseFloat(item), 0).toFixed(2)} displayType='text' thousandSeparator={true} prefix='$' />
          </span>
        </div>
      ),
    },
    {
      text: "Expiry Date",
      dataField: "expiryDate",
      sort: true,
      formatter: (cellContent, sub) => (
        <h5 className="font-size-16 mb-1">
          {sub.expiryDate !== null && new Date(sub.expiryDate).toLocaleDateString('en-GB')}
        </h5>
      ),
      footer: ''
    },
    {
      text: "Comment",
      dataField: "comment",
      sort: true,
      formatter: (cellContent, sub) => (
        <div className='slname-width'>
          <h5 className="font-size-16 mb-1">{sub.comment}</h5>
        </div>
      ),
      footer: ''
    },
    {
      dataField: "menu",
      isDummyField: true,
      editable: false,
      text: "Action",
      formatter: (cellContent, sub) => (
        <div className="d-flex gap-3">
          {profileData.accType === 'Admin' && 
            <>
              <Link className="text-success" to="#">
                <i
                  className="mdi mdi-pencil font-size-18"
                  id="edittooltip"
                  onClick={() => handleSubClickEdit(sub)}
                ></i>
              </Link>
              <Link className="text-danger" to="#">
                <i
                  className="mdi mdi-delete font-size-18"
                  id="deletetooltip"
                  onClick={() => handleSubClickDelete(sub)}
                ></i>
              </Link>
            </>
          }
        </div>
      ),
      footer: ''
    },
  ];
  /**
   * An array specifying the default sorting configuration for a table.
   * Each object in the array defines the column to sort by (`dataField`)
   * and the sort order (`order`), which can be either 'asc' for ascending
   * or 'desc' for descending. If the specified `dataField` does not match
   * any defined column, it will be ignored.
   *
   * @type {Array<{dataField: string, order: 'asc' | 'desc'}>}
   */
  const defaultSorted = [
    {
      dataField: 'service', // if dataField is not match to any column you defined, it will be ignored.
      order: 'asc' // desc or asc
    }
  ];
  const paymentOptions = [...new Set(subs.map(u => u.paymentOption))].map(type => ({ key: type, label: type, icon: '', colorClass: 'bg-primary' }));
  return (
    <React.Fragment>
      <PageLoader progress={progress} step={step} visible={visible} />
      <div className='page-content'>
        <MetaTags>
          <title>Subcriptions List </title>
        </MetaTags>
        <Container fluid>
          {profileData.permissions.includes('domainsAccess') ? (
            <>
              <Breadcrumbs title='Marketing' breadcrumbItem='Subcriptions List' />
              <Row className='card-section'>
                <Col lg='12'>
              <div className='pcss3t logtb pcss3t-effect-scale pcss3t-theme-1'>
                <ul>
                  <AnimatePresence key='1' exitBeforeEnter>
                    <motion.li
                      className={`tab-content tab-content-1 typography`}
                      initial={{ opacity: 0, x: 30 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -30 }}
                      transition={{ duration: 0.3 }}
                      style={{ display: 'block' }}
                    >
                      <div id='card-section' ref={cardRef}>
                        <Row className='card-section'>
                          <Col lg='12'>
                            <Card>
                              <CardBody className='cardBodySticky'>
                                <ToolkitProvider
                                  keyField='_id'
                                  columns={columns}
                                  data={filteredSubs.length > 0 ? filteredSubs : subs}
                                  search
                                >
                                  {(props) => (
                                    <React.Fragment>
                                      <MainContent
                                        page={{ 
                                          pg: 'subs', 
                                          cardHeight, 
                                          props: props,
                                          showSearch: true, 
                                          showCSV: true, 
                                          showInTblFltr: true, 
                                          showDbFilter: false, 
                                          showBtn: true, 
                                          columns: props.baseProps.columns,
                                          data: props.baseProps.data,
                                          defaultSorted,
                                        }}
                                        // filter options
                                        filterOptions={{ paymentOptions: paymentOptions }}
                                        setNoDbFilters={setNoDbFilters}
                                        // options for adding/updating modal and button
                                        btnCrtModalOptions={{ 
                                          btnLabelCrt: 'ADD SUBSCRIPTION',
                                          modalSize:'xl',
                                          modalTitle: isEdit ? 'Edit Subcription' : 'Add Subcription',
                                          modalBtnLabel: isEdit ? 'Edit Subcription' : 'Add Subcription',
                                          modalAdd: modalNewSub,
                                          toggleAddModal: toggleNewSubModal,
                                          handleNewClick: handleNewSubClick,
                                          modalContent: modalSubContent,
                                          handleSubmit: handleSubSubmit,
                                          errorMsg: error
                                        }}
                                        // table data opotions
                                        tableOptions={{ 
                                          includeSelect: false, 
                                          calculateFooter: true, 
                                          manualColumnWidthsIndex: [2,4,5,7], 
                                          manualColumnWidths: [650,120,120,150] 
                                        }}
                                      />
                                      <SweetAlert
                                        show={showDeleteAlert}
                                        danger
                                        showCancel
                                        confirmBtnText='Yes, delete it!'
                                        confirmBtnBsStyle='danger'
                                        title='Are you sure?'
                                        onConfirm={handleDeleteSub}
                                        onCancel={() => setShowDeleteAlert(false)}
                                        focusCancelBtn
                                      >
                                        <p className='fw-bold'>{sub.service}</p>{' '}
                                        will be deleted from the system!
                                      </SweetAlert>
                                    </React.Fragment>
                                  )}
                                </ToolkitProvider>
                              </CardBody>
                            </Card>
                          </Col>
                        </Row>
                      </div>
                    </motion.li>
                  </AnimatePresence>
                </ul>
              </div>
                </Col>
              </Row>
            </>
          ) : (
            <Alert color="danger">{" "} You do not have access to this page.{" "}</Alert>
          )}
        </Container>
      </div>
    </React.Fragment>
  );
}